import { ProductProgress } from '@lemoncloud/codes-goods-api';

export type DeploymentState = 'github' | 'build' | 'deploy' | 'split';
export type StepStatus = 'pending' | 'running' | 'completed' | 'failed';

// Deployment status constants
export const DEPLOYMENT_STATUS = {
    FAILURE: 'failure',
    SUCCESS: 'success',
    PENDING: 'pending',
    RUNNING: 'running',
} as const;

export const DEPLOYMENT_CODE = {
    DEPLOY_SUCCESS: 'deploy.success',
    DEPLOY_FAILURE: 'deploy.failure',
} as const;

// Stage key to display name mapping
export const STAGE_DISPLAY_NAMES: Record<DeploymentState, string> = {
    github: 'Download',
    build: 'Build',
    deploy: 'Deploy',
    split: 'Split',
};

// Type guards for progress status
export const isProgressFailure = (progress?: ProductProgress): boolean => {
    return progress?.status === DEPLOYMENT_STATUS.FAILURE;
};

export const isProgressSuccess = (progress?: ProductProgress): boolean => {
    return progress?.code === DEPLOYMENT_CODE.DEPLOY_SUCCESS;
};

// Validate if array contains only valid DeploymentState values
const isValidStatesArray = (arr: unknown): arr is DeploymentState[] => {
    if (!Array.isArray(arr) || arr.length === 0) {
        return false;
    }
    const validStates: DeploymentState[] = ['github', 'build', 'deploy', 'split'];
    return arr.every(state => validStates.includes(state as DeploymentState));
};

// Generate deployment steps dynamically from states array
export const getDeploymentSteps = (states?: unknown): Array<{ name: string; key: DeploymentState }> => {
    // Default fallback for legacy products without states array
    const defaultStates: DeploymentState[] = ['github', 'build', 'deploy'];
    const activeStates = isValidStatesArray(states) ? states : defaultStates;

    return activeStates.map(key => ({
        key,
        name: STAGE_DISPLAY_NAMES[key],
    }));
};

// Calculate progress value dynamically based on total stages
export const getProgressValue = (progress?: ProductProgress, totalStages: number = 3): number => {
    if (!progress?.code) {
        return 0;
    }

    const [stateKey, statusKey] = progress.code.split('.');
    const states = progress.states && progress.states.length > 0 ? progress.states : ['github', 'build', 'deploy'];
    const stageIndex = states.indexOf(stateKey as DeploymentState);

    if (stageIndex === -1) {
        return 0;
    }

    // Calculate progress based on total stages
    const stageWeight = 100 / totalStages;
    const baseProgress = stageIndex * stageWeight;

    // Add status offset within the stage
    const statusOffsets: Record<string, number> = {
        pending: 0.1,
        running: 0.5,
        success: 1.0,
    };

    const offset = (statusOffsets[statusKey] || 0) * stageWeight;
    return Math.min(100, Math.floor(baseProgress + offset));
};

// Get step status based on current progress
export const getStepStatus = (
    stepIndex: number,
    progress?: ProductProgress,
    deploymentSteps: Array<{ key: DeploymentState }> = []
): StepStatus => {
    if (!progress?.code || deploymentSteps.length === 0) {
        return 'pending';
    }

    const currentStepIndex = deploymentSteps.findIndex(step => progress.code?.includes(step.key));

    if (currentStepIndex === -1) {
        return 'pending';
    }

    if (stepIndex < currentStepIndex) {
        return 'completed';
    }
    if (stepIndex > currentStepIndex) {
        return 'pending';
    }

    // Current step - check status from code
    if (progress.code.includes('success')) {
        return 'completed';
    }
    if (progress.code.includes('running')) {
        return 'running';
    }
    if (progress.code.includes('failure')) {
        return 'failed';
    }

    return 'pending';
};

// Get step circle className based on status
export const getStepClassName = (status: StepStatus): string => {
    const baseClass = 'shrink-0 w-[22px] h-[22px] rounded-full flex items-center justify-center text-xs font-medium';
    const statusClasses: Record<StepStatus, string> = {
        completed: 'bg-[#40C253]',
        running: 'bg-[#357AFF] text-white',
        failed: 'bg-error text-white',
        pending: 'bg-gray-200 text-gray-500',
    };
    return `${baseClass} ${statusClasses[status]}`;
};

// Get step text className based on status
export const getStepTextClassName = (status: StepStatus): string => {
    const textClasses: Record<StepStatus, string> = {
        completed: 'text-[#40C253]',
        running: 'text-[#357AFF]',
        failed: 'text-error',
        pending: 'text-gray-500',
    };
    return `text-xs font-medium ${textClasses[status]}`;
};
